import { useEffect, useState } from "react";
import { toast } from "sonner";
import { Input } from "@/components/ui/input";
import { api, errMsg } from "@/lib/api";
import { PageTitle, Panel, Btn, Sel, StatusBadge } from "@/admin/ui";

const TYPES = [["duplicate", "A · Exact duplicate"], ["similar", "B · Substantially similar"], ["complementary", "C · Complementary"], ["contradictory", "E · Contradictory"]];
const blank = { type: "duplicate", rule_ids: [], primary_rule_id: "", resolution: "use_primary", note: "", status: "draft" };

export default function Reconciliation() {
  const [rules, setRules] = useState([]);
  const [groups, setGroups] = useState([]);
  const [g, setG] = useState(blank);
  const [q, setQ] = useState("");
  const load = () => { api.get("/admin/rules").then((r) => setRules(r.data)); api.get("/admin/groups").then((r) => setGroups(r.data)); };
  useEffect(load, []);
  const code = (id) => rules.find((r) => r.id === id)?.code || id.slice(0, 8);
  const toggle = (id) => setG({ ...g, rule_ids: g.rule_ids.includes(id) ? g.rule_ids.filter((x) => x !== id) : [...g.rule_ids, id] });
  const create = async () => {
    try { await api.post("/admin/groups", { ...g, primary_rule_id: g.primary_rule_id || null }); toast.success("Group saved as draft"); setG(blank); load(); } catch (e) { toast.error(errMsg(e)); }
  };
  const approve = async (grp, status) => {
    try { await api.put(`/admin/groups/${grp.id}`, { ...grp, status }); toast.success(`Group ${status}`); load(); } catch (e) { toast.error(errMsg(e)); }
  };
  const shown = rules.filter((r) => !q || `${r.code} ${r.target} ${r.category}`.toLowerCase().includes(q.toLowerCase()));

  return (
    <div className="space-y-6">
      <PageTitle title="Overlaps & Conflicts" sub="Record how overlapping interpretations from the three documents relate. Approved groups are applied deterministically. Reinforcing themes (D) are detected automatically from shared theme tags. Unresolved contradictions block report release." />
      <Panel title="New reconciliation group" testId="group-form">
        <div className="grid gap-3 sm:grid-cols-3">
          <Sel value={g.type} onChange={(v) => setG({ ...g, type: v })} options={TYPES} testId="group-type-select" />
          {g.type === "contradictory" && <Sel value={g.resolution} onChange={(v) => setG({ ...g, resolution: v })} options={[["use_primary", "Resolve: use primary (documented hierarchy)"], ["unresolved", "Unresolved — flag for review"]]} testId="group-resolution-select" />}
          <Input placeholder="Note / documented basis" value={g.note} onChange={(e) => setG({ ...g, note: e.target.value })} data-testid="group-note-input" />
        </div>
        <Input placeholder="Filter rules" value={q} onChange={(e) => setQ(e.target.value)} className="mt-3 max-w-xs" />
        <div className="mt-3 max-h-60 space-y-1 overflow-y-auto rounded border border-border p-2 text-sm">
          {shown.map((r) => (
            <label key={r.id} className="flex items-center gap-3 rounded px-2 py-1 hover:bg-secondary/40">
              <input type="checkbox" checked={g.rule_ids.includes(r.id)} onChange={() => toggle(r.id)} data-testid={`group-rule-${r.id}`} />
              <input type="radio" name="primary" disabled={!g.rule_ids.includes(r.id)} checked={g.primary_rule_id === r.id} onChange={() => setG({ ...g, primary_rule_id: r.id })} title="Primary" />
              <span className="font-mono-ui text-xs">{r.code || r.id.slice(0, 8)}</span> <span className="text-muted-foreground">{r.feature_type} {r.target} · {r.source?.document_title || "—"}</span>
            </label>
          ))}
          {!rules.length && <p className="p-2 text-muted-foreground">No rules available yet.</p>}
        </div>
        <p className="mt-2 text-xs text-muted-foreground">Tick the rules in the group; use the radio button to mark the primary (retained) rule.</p>
        <Btn className="mt-3" disabled={g.rule_ids.length < 2} onClick={create} data-testid="group-save-btn">Save group</Btn>
      </Panel>
      <Panel title={`Groups (${groups.length})`} testId="groups-list">
        <ul className="divide-y divide-border text-sm">
          {groups.map((x) => (
            <li key={x.id} className="flex flex-wrap items-center justify-between gap-2 py-3">
              <span><StatusBadge s={x.status} /> <strong className="ml-2">{x.type}</strong> · {x.rule_ids.map(code).join(", ")} {x.primary_rule_id && <span className="text-muted-foreground">· primary {code(x.primary_rule_id)}</span>} <span className="text-muted-foreground">{x.note}</span></span>
              <span className="flex gap-2">
                {x.status !== "approved" && <Btn variant="ghost" onClick={() => approve(x, "approved")} data-testid={`approve-group-${x.id}`}>Approve</Btn>}
                {x.status !== "archived" && <Btn variant="ghost" onClick={() => approve(x, "archived")}>Archive</Btn>}
              </span>
            </li>
          ))}
          {!groups.length && <li className="py-3 text-muted-foreground">No groups yet.</li>}
        </ul>
      </Panel>
    </div>
  );
}
